import {PartialRuleBaseModelStrict, RuleCompoundConditionTypesEnum} from '../../models/api/ruleModel';
import {WithClientId} from '../editors/commons/clientIdModel';
import {getEditorValidationErrorFromPath} from '../editors/commons/editorErrors';
import {isSimpleCompoundConditions} from '../editors/conditions/compoundConditionModel';
import {Expression} from '../editors/expressions/expressionModel';
import {getExpressionRefsValidationErrors} from '../editors/expressions/expressionValidationHelpers';
import {ResultTypesEnum} from '../editors/expressions/infer/internal';
import {fromDefinitionItem, getParamsErrors, typeAndParamsEqual} from './ruleActionConditionHelpers';
import {
  ANDConditions,
  ClientRuleCondition,
  CompareExpressionCondition,
  RuleCondition,
  RuleConditionTypesEnum,
} from './ruleConditionModel';
import {RuleEditorConditionErrorsEnum} from './ruleEditor/ruleEditorErrors';
import {RuleIntegration} from './ruleIntegrationModel';
import {RuleConditionDefinition} from './ruleParameterModel';

/**
 * Older conditions have top-level 'OR', which is not supported anymore.
 * This function converts them to have an 'AND' operator at the top-level
 */
export function convertORConditions<T extends PartialRuleBaseModelStrict>(rule: T): T {
  if (
    !rule.compoundConditions ||
    rule.compoundConditions[0] === RuleCompoundConditionTypesEnum.AND ||
    !isSimpleCompoundConditions(rule.compoundConditions)
  ) {
    return rule;
  }

  return {
    ...rule,
    compoundConditions: [RuleCompoundConditionTypesEnum.AND, rule.compoundConditions],
  };
}

// Build a generic empty / default condition from a definition spec.
export function fromDefinitionCondition(
  definitionCondition: RuleConditionDefinition,
  clientId?: string,
): ClientRuleCondition {
  return fromDefinitionItem(definitionCondition, clientId);
}

export function checkClientRuleConditionsEquality(a: ClientRuleCondition, b: ClientRuleCondition) {
  return typeAndParamsEqual(a, b);
}

export function checkANDConditionsEquality(a: ANDConditions, b: ANDConditions): boolean {
  if (a.length !== b.length) {
    return false;
  }

  // Loop through all conds[].
  for (let i = 0; i < a.length; i++) {
    const condsA = a[i];
    const condsB = b[i];

    if (condsA.length !== condsB.length) {
      return false;
    }

    // Same cond[] reference, skip the more in-depth checks.
    if (condsA === condsB) {
      continue;
    }

    // Loop through all conds.
    for (let j = 0; j < condsA.length; j++) {
      const condA = condsA[j];
      const condB = condsB[j];

      // Same cond reference.
      if (condA === condB) {
        continue;
      }

      if (!typeAndParamsEqual(condA, condB)) {
        return false;
      }
    }
  }

  return true;
}

export function getConditionErrors(
  condition: RuleCondition,
  conditionDefinition: RuleConditionDefinition | undefined,
  expressions: ReadonlyArray<Expression>,
): ReadonlyArray<RuleEditorConditionErrorsEnum> {
  if (!('params' in condition)) {
    return [];
  }

  // Fallback on approximate validation.
  if (!conditionDefinition) {
    console.warn('No definition found for condition', condition);
    return getParamsErrors(condition);
  }

  const errors = conditionDefinition.validate(condition.params);
  if (errors.length > 0) {
    return errors.map((error) => getEditorValidationErrorFromPath(error.path));
  }

  return getExpressionRefsValidationErrors(condition.params, expressions);
}

export function checkConditionValidity(
  condition: RuleCondition,
  ruleIntegration: RuleIntegration,
  expressions: ReadonlyArray<Expression>,
): boolean {
  return (
    getConditionErrors(condition, ruleIntegration.findConditionDefinition(condition), expressions).length ===
    0
  );
}

export function checkANDConditionsValidity(
  andConditions: ANDConditions,
  ruleIntegration: RuleIntegration,
  expressions: ReadonlyArray<Expression>,
) {
  return andConditions.every((orConditions) =>
    orConditions.every((condition) => checkConditionValidity(condition, ruleIntegration, expressions)),
  );
}

type ConditionFromType<
  TCondition extends RuleCondition,
  TConditionType extends RuleCondition['type'],
> = TCondition extends {type: TConditionType} ? TCondition : never;

export function buildRuleCondition<
  TConditionType extends RuleCondition['type'],
  TCondition extends ConditionFromType<RuleCondition, TConditionType>,
>(type: TConditionType, params: TCondition['params']): TCondition {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return {
    type,
    integration_id: 0,
    params,
  } as TCondition;
}

export function createConditionWithParamsAndClientId<TCondition extends RuleCondition>(
  conditionDefinition: RuleConditionDefinition,
  params: TCondition['params'],
  stableClientId: string,
): WithClientId<TCondition> {
  const condition = fromDefinitionCondition(conditionDefinition, stableClientId);

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return {
    ...condition,
    params,
  } as any;
}

const expressionResultToConditionType: Partial<Record<ResultTypesEnum, CompareExpressionCondition['type']>> =
  {
    [ResultTypesEnum.BOOLEAN]: RuleConditionTypesEnum.COMPARE_EXPRESSION_BOOLEAN,
    [ResultTypesEnum.NUMBER]: RuleConditionTypesEnum.COMPARE_EXPRESSION_NUMBER,
    [ResultTypesEnum.DATETIME]: RuleConditionTypesEnum.COMPARE_EXPRESSION_DATETIME,
    [ResultTypesEnum.STRING]: RuleConditionTypesEnum.COMPARE_EXPRESSION_STRING,
    [ResultTypesEnum.URL]: RuleConditionTypesEnum.COMPARE_EXPRESSION_STRING,
    [ResultTypesEnum.EMAIL]: RuleConditionTypesEnum.COMPARE_EXPRESSION_STRING,
    [ResultTypesEnum.TEAMMATE]: RuleConditionTypesEnum.COMPARE_EXPRESSION_TEAMMATE,
    [ResultTypesEnum.LIST]: RuleConditionTypesEnum.COMPARE_EXPRESSION_LIST,
  };

export function buildConditionTypeFromExpression(expression: Expression) {
  const {type} = expression.inferredType;

  return expressionResultToConditionType[type] ?? RuleConditionTypesEnum.COMPARE_EXPRESSION_OTHER;
}
